/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { useEffect, useState } from 'react';
import { clsx } from 'clsx';

interface SkeletonProps {
  className?: string;
  variant?: 'line' | 'circle' | 'card' | 'avatar';
}

function Skeleton({ className, variant = 'line' }: SkeletonProps) {
  const base = 'animate-pulse bg-navy-100 rounded';
  const variantClass = {
    line: 'h-3.5 rounded',
    circle: 'rounded-full',
    card: 'rounded-2xl',
    avatar: 'w-9 h-9 rounded-full',
  }[variant];

  // Pulsing shapes carry no information. Hiding them keeps a SkeletonRegion's
  // announcement to the one "Loading" it means, instead of a dozen blank nodes.
  return <div aria-hidden="true" className={clsx(base, variantClass, className)} />;
}

/**
 * Wrapper that makes a block of skeletons audible.
 *
 * Accessibility survey `08-accessibility.md` #17: skeletons rendered and were
 * swapped for content with no announcement, so a screen reader user could not
 * tell whether a page was still loading, finished and empty, or failed. The
 * live region belongs on the block, not on each shape — one `role="status"`
 * per loading area, so the announcement is "Loading your dashboard" once
 * rather than N times.
 *
 * Failures are already covered: `LoadErrorCard` carries `role="alert"`.
 */
function SkeletonRegion({
  children,
  label = 'Loading',
  className,
}: {
  children: React.ReactNode;
  /** What is loading, from the reader's point of view. */
  label?: string;
  className?: string;
}) {
  /*
   * The region mounts with an EMPTY label and fills it on the next commit.
   * Every caller renders <SkeletonRegion> conditionally (`if (loading) return
   * <DashboardSkeleton/>`), so it cannot stay mounted across the load — and a
   * live region inserted with its text already present is announced
   * inconsistently by NVDA and JAWS. Splitting the insert from the text change
   * makes it a mutation of a region already in the accessibility tree, which
   * is the same reason Layout's route announcer carries no `key`.
   */
  const [announced, setAnnounced] = useState('');
  useEffect(() => {
    setAnnounced(label);
  }, [label]);

  return (
    <div role="status" aria-live="polite" aria-busy="true" className={className}>
      {children}
      {/*
        Last, not first: under a `space-y-*` wrapper (`> * + *`) a leading
        span would push the first skeleton down by a full gap. `sr-only` is
        absolutely positioned, so as the last child it shifts nothing.
      */}
      <span className="sr-only">{announced}</span>
    </div>
  );
}

function SkeletonTableRow() {
  return (
    <div className="flex items-center gap-4 px-5 py-4">
      <Skeleton variant="avatar" />
      <div className="flex-1 space-y-2">
        <Skeleton className="w-48 h-3.5" />
        <Skeleton className="w-24 h-3" />
      </div>
      <Skeleton className="h-5 w-16 rounded-full" />
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="rounded-2xl border border-surface-border overflow-hidden">
      <Skeleton className="w-full h-36" variant="card" />
      <div className="p-4 space-y-2">
        <Skeleton className="w-3/4 h-4" />
        <Skeleton className="w-1/2 h-3" />
      </div>
    </div>
  );
}

export { Skeleton, SkeletonTableRow, SkeletonCard, SkeletonRegion };
export default Skeleton;
